import { supabase, UserProfile } from '../lib/supabase'

// Demo data for when Supabase is not configured
const demoUserProfiles: UserProfile[] = [
  {
    id: '1',
    name: 'John Doe',
    mobile: '+91-9876543210',
    email: 'john.doe@example.com',
    user_status: 'pending',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Jane Smith',
    mobile: '+91-9876543211',
    email: 'jane.smith@example.com',
    user_status: 'approved',
    created_at: new Date(Date.now() - 86400000).toISOString(),
    updated_at: new Date(Date.now() - 86400000).toISOString()
  },
  {
    id: '3',
    name: 'Mike Johnson',
    mobile: '+91-9876543212',
    email: 'mike.johnson@example.com',
    user_status: 'rejected',
    created_at: new Date(Date.now() - 172800000).toISOString(),
    updated_at: new Date(Date.now() - 172800000).toISOString()
  },
  {
    id: '4',
    name: 'Sarah Wilson',
    mobile: '+91-9876543213',
    email: 'sarah.wilson@example.com',
    user_status: 'paused',
    created_at: new Date(Date.now() - 259200000).toISOString(),
    updated_at: new Date(Date.now() - 259200000).toISOString()
  }
]

// Check if we're in demo mode
const isDemoMode = !process.env.REACT_APP_SUPABASE_URL || 
                   process.env.REACT_APP_SUPABASE_URL === 'https://placeholder.supabase.co'

export const userService = {
  // Get all user profiles
  async getAllUserProfiles(): Promise<UserProfile[]> {
    if (isDemoMode) {
      // Return demo data with a small delay to simulate API call
      await new Promise(resolve => setTimeout(resolve, 500))
      return [...demoUserProfiles]
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      throw error
    }

    return data || []
  },

  // Update user profile status
  async updateUserStatus(id: string, status: UserProfile['user_status']): Promise<UserProfile> {
    if (isDemoMode) {
      // Update demo data
      const userProfile = demoUserProfiles.find(u => u.id === id)
      if (userProfile) {
        userProfile.user_status = status
        userProfile.updated_at = new Date().toISOString()
        await new Promise(resolve => setTimeout(resolve, 500))
        return userProfile
      }
      throw new Error('User profile not found')
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        user_status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  },

  // Get user profile by ID
  async getUserProfileById(id: string): Promise<UserProfile | null> {
    if (isDemoMode) {
      return demoUserProfiles.find(u => u.id === id) || null
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      return null
    }

    return data
  }
}
